import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FAIcon,
} from '@/components'
import { Button } from '@/components/ui'
import { Textarea } from '@/components/ui/textarea'
import { format } from '@/utils/date-fns'
import type { FileAttachment } from '@/utils/hooks'
import { useConfirm, usePermissions, useUser } from '@/utils/hooks'
import type { FontAwesomeIcon } from '@ur-solutions/react-components'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useId, useRef, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import type { ArchivedInfringementForm } from '../types'
import { INFRINGEMENT_LEADER_PERMISSIONS } from '../consts'
import { InfringementDetailCard } from './detail/detail-card'
import { DetailPill, DetailTextBlock } from './detail/detail-header'
import { ArchiveInfringementPrompt } from './table/ArchiveInfringementModal'

export interface FollowUpInfringement {
  id: string
  user: { id: string }
  solution?: string | null
  /** A receipt given on a related issue before the infringement had its own;
   * it stands in for the explanation until the infringement is receipted. */
  issueReason?: string | null
  leadersComment?: string | null
  archivedAt?: string | null
  closedAt?: string | null
  wronglyGenerated?: boolean
  attachments: { id: string; name: string; file?: string | null }[]
  actionLogs: {
    id: string
    action: string
    attachmentName?: string | null
    createdAt: string
    user?: { fullName: string } | null
    secondaryUser?: { fullName: string } | null
  }[]
}

export interface FollowUpChanges {
  solution?: string
  leadersComment?: string
  attachmentsAdd?: Omit<FileAttachment, 'id'>[]
  attachmentsRemove?: string[]
}

type OnPatch = (changes: FollowUpChanges) => Promise<unknown>

function useCanChangeInfringement() {
  const { hasPermissions } = usePermissions()
  return hasPermissions(INFRINGEMENT_LEADER_PERMISSIONS, true)
}

/** Drivers acknowledge ("kvitter") their own infringement, with its
 * explanation and attachments, without the change permission; the leader's
 * comment stays a leader action. Nothing is receipted once it is closed. */
function useCanReceipt(infringement: FollowUpInfringement) {
  const me = useUser()
  const canChange = useCanChangeInfringement()
  return !infringement.closedAt && (canChange || infringement.user.id === me.id)
}

/** Where the follow-up stands, as the combined list counts it: archived,
 * closed, handled once receipted or documented, otherwise open. */
export const InfringementStatusPills: React.FC<{
  infringement: FollowUpInfringement
}> = ({ infringement }) => {
  const translations = useTranslate({
    open: 'common.open-alt',
    started: 'issues.started',
    closed: 'common.closed',
    archived: 'common.archived',
  })
  const handled =
    !!(infringement.solution || infringement.issueReason) ||
    infringement.attachments.length > 0

  if (infringement.archivedAt)
    return <DetailPill tone="stone">{translations.archived}</DetailPill>
  if (infringement.closedAt)
    return <DetailPill tone="emerald">{translations.closed}</DetailPill>
  if (handled)
    return <DetailPill tone="amber">{translations.started}</DetailPill>
  return <DetailPill tone="rose">{translations.open}</DetailPill>
}

interface FollowUpTextFieldProps {
  label: string
  /** The stored text; the button stays disabled until the draft differs. */
  saved: string
  /** What the field starts with, when that is not the stored text. */
  initial?: string
  editable: boolean
  submitText: string
  submitIcon: FontAwesomeIcon
  allowEmpty?: boolean
  onSave(value: string): Promise<unknown>
}

/** Shown as text; whoever may write it opens a textarea with the edit button,
 * which the submit button replaces until the text is saved or cancelled. */
const FollowUpTextField: React.FC<FollowUpTextFieldProps> = ({
  label,
  saved,
  initial = saved,
  editable,
  submitText,
  submitIcon,
  allowEmpty = false,
  onSave,
}) => {
  const translations = useTranslate({
    edit: 'common.edit',
    cancel: 'common.cancel',
  })
  const id = useId()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(initial)
  const [syncedFrom, setSyncedFrom] = useState(initial)
  const [saving, setSaving] = useState(false)

  if (initial !== syncedFrom) {
    setSyncedFrom(initial)
    setDraft(initial)
  }

  if (!editable || !editing)
    return (
      <DetailTextBlock
        label={label}
        action={
          editable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
            >
              <FAIcon icon="pen" />
              {translations.edit}
            </Button>
          )
        }
      >
        {initial || '-'}
      </DetailTextBlock>
    )

  const value = draft.trim()
  const canSave =
    !saving && value !== saved.trim() && (allowEmpty || value !== '')

  function cancel() {
    setDraft(initial)
    setEditing(false)
  }

  async function save() {
    setSaving(true)
    try {
      await onSave(value)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-t border-stone-200 px-5 py-4 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="text-xs font-semibold text-stone-500 uppercase"
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" disabled={saving} onClick={cancel}>
            {translations.cancel}
          </Button>
          <Button size="sm" disabled={!canSave} onClick={() => void save()}>
            <FAIcon icon={submitIcon} />
            {submitText}
          </Button>
        </div>
      </div>
      <Textarea
        id={id}
        className="mt-1.5 min-h-20 text-sm"
        value={draft}
        disabled={saving}
        autoFocus
        onChange={setDraft}
      />
    </div>
  )
}

/** The driver's explanation and the leader's comment at the bottom of the
 * detail card, written in place by whoever may write them. */
export const InfringementFollowUpBlocks: React.FC<{
  infringement: FollowUpInfringement
  onPatch: OnPatch
}> = ({ infringement, onPatch }) => {
  const translations = useTranslate({
    explanation: 'common.explaination',
    receipt: 'infringements.receipt',
    commentFromLeader: 'infringements.comment-from-leader',
    save: 'common.save',
  })
  const canChange = useCanChangeInfringement()
  const canReceipt = useCanReceipt(infringement)

  return (
    <>
      <FollowUpTextField
        label={translations.explanation}
        saved={infringement.solution ?? ''}
        initial={infringement.solution ?? infringement.issueReason ?? ''}
        editable={canReceipt}
        submitText={translations.receipt}
        submitIcon="receipt"
        onSave={solution => onPatch({ solution })}
      />
      <FollowUpTextField
        label={translations.commentFromLeader}
        saved={infringement.leadersComment ?? ''}
        editable={canChange}
        submitText={translations.save}
        submitIcon="comment"
        allowEmpty
        onSave={leadersComment => onPatch({ leadersComment })}
      />
    </>
  )
}

/** Where a detail page puts its actions: beside the title, or under it on
 * phones, where there is no room beside it. */
export function infringementActionsPlacement(actions: React.ReactNode) {
  return isMobileOnly ? { headerActions: actions } : { headerRight: actions }
}

interface ActionsProps {
  closed: boolean
  archived: boolean
  busy: boolean
  onSetClosed(closed: boolean): Promise<unknown>
  onSetArchived(archived: boolean, archivedBasis?: string): Promise<unknown>
  /** Further `DropdownMenuItem`s for the "more" menu. */
  menuItems?: React.ReactNode
}

/**
 * The page header's actions, like the Absence page's: closing as a button,
 * the less frequent ones in a menu. The receipt and the leader's comment are
 * written in the detail card.
 */
export const InfringementActions: React.FC<ActionsProps> = ({
  closed,
  archived,
  busy,
  onSetClosed,
  onSetArchived,
  menuItems,
}) => {
  const translations = useTranslate({
    close: 'common.close',
    reopen: 'common.reopen',
    archive: 'common.archive',
    restore: 'common.restore',
    more: 'infringements.more-actions',
  })
  const addPrompt = usePrompt()
  const canChange = useCanChangeInfringement()

  async function toggleArchived() {
    if (archived) {
      await onSetArchived(false)
      return
    }
    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => <ArchiveInfringementPrompt onResolve={resolve} />
    )
    if (!data) return
    await onSetArchived(true, data.archivedBasis)
  }

  const showMenu = canChange || !!menuItems

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon-md"
              aria-label={translations.more}
              disabled={busy}
            >
              <FAIcon icon="ellipsis-h" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canChange && (
              <DropdownMenuItem onClick={() => void toggleArchived()}>
                <FAIcon icon="archive" fixedWidth />
                {archived ? translations.restore : translations.archive}
              </DropdownMenuItem>
            )}
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {canChange && (
        <Button
          variant="outline"
          size="lg"
          disabled={busy}
          onClick={() => void onSetClosed(!closed)}
        >
          <FAIcon icon="check" />
          {closed ? translations.reopen : translations.close}
        </Button>
      )}
    </div>
  )
}

/** The infringement's files; whoever may receipt it adds and removes them in
 * place. */
export const InfringementAttachmentsCard: React.FC<{
  infringement: FollowUpInfringement
  onPatch: OnPatch
}> = ({ infringement, onPatch }) => {
  const translations = useTranslate({
    attachments: 'common.attachments',
    addAttachments: 'common.add-attachments',
    download: 'common.download',
    remove: 'common.remove',
    noAttachments: 'common.no-attachments',
    deleteAttachment: ['modal.delete-attachment', { title: 'title' }],
    deleteAttachmentTitle: 'modal.delete-attachment-title',
  })
  const confirm = useConfirm()
  const editable = useCanReceipt(infringement)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const { attachments } = infringement

  async function patch(changes: FollowUpChanges) {
    setSaving(true)
    try {
      await onPatch(changes)
    } finally {
      setSaving(false)
    }
  }

  function handleFileChange(evt: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(evt.target.files ?? [])
    evt.target.value = ''
    if (!files.length) return
    void patch({
      attachmentsAdd: files.map(file => ({ name: file.name, file })),
    })
  }

  async function remove(attachment: FollowUpInfringement['attachments'][0]) {
    const { data: confirmed } = await confirm(
      translations.deleteAttachment({ title: attachment.name }),
      translations.deleteAttachmentTitle
    )
    if (!confirmed) return
    await patch({ attachmentsRemove: [attachment.id] })
  }

  return (
    <InfringementDetailCard
      type="attachments"
      title={translations.attachments}
      icon="paperclip"
      headerRight={
        editable && (
          // The header collapses the card on click.
          <span onClick={evt => evt.stopPropagation()}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => fileInputRef.current?.click()}
            >
              <FAIcon icon="plus" />
              {translations.addAttachments}
            </Button>
          </span>
        )
      }
    >
      {attachments.length ? (
        <ul className="divide-y divide-stone-200">
          {attachments.map(attachment => (
            <li
              key={attachment.id}
              className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            >
              <p className="truncate font-medium text-stone-900">
                {attachment.name}
              </p>
              <div className="flex items-center gap-2 self-start sm:self-auto">
                {attachment.file && (
                  <a
                    href={attachment.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 transition hover:bg-stone-100"
                  >
                    <FAIcon icon="download" type="regular" />
                    {translations.download}
                  </a>
                )}
                {editable && (
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label={translations.remove}
                    disabled={saving}
                    onClick={() => void remove(attachment)}
                  >
                    <FAIcon icon="trash" type="regular" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-5 text-sm text-stone-500 sm:px-6">
          {translations.noAttachments}
        </p>
      )}
    </InfringementDetailCard>
  )
}

export type FollowUpActionLog = FollowUpInfringement['actionLogs'][number]

/** One action log entry as a sentence, e.g. "Kvittert av Ola Sjåfør". Shared by
 * the detail page's log and the infringement list's action column. */
export function useFollowUpActionText() {
  const translations = useTranslate({
    system: 'common.system',
    reminderSent: 'infringements.reminder-sent',
    reminderSentTo: [
      'issues.reminder-by-user-to-user',
      { user: 'user', target: 'target' },
    ],
    actions: {
      CREATED: ['issues.created-by-user', { user: 'user' }],
      UPDATED: ['issues.updated-by-user', { user: 'user' }],
      ARCHIVED: ['issues.archived-by-user', { user: 'user' }],
      RESTORED: ['issues.restored-by-user', { user: 'user' }],
      CLOSED: ['issues.closed-by-user', { user: 'user' }],
      REOPENED: ['issues.reopened-by-user', { user: 'user' }],
      RECEIPTED: ['issues.receipted-by-user', { user: 'user' }],
      ATTACHMENT_ADDED: [
        'infringements.attachment-added-by-user',
        { user: 'user', name: 'name' },
      ],
      ATTACHMENT_REMOVED: [
        'infringements.attachment-removed-by-user',
        { user: 'user', name: 'name' },
      ],
      LEADER_COMMENT_UPDATED: [
        'issues.leader-comment-updated-by-user',
        { user: 'user' },
      ],
      MARKED_WRONGLY_GENERATED: [
        'infringements.marked-wrongly-generated-by-user',
        { user: 'user' },
      ],
      UNMARKED_WRONGLY_GENERATED: [
        'infringements.unmarked-wrongly-generated-by-user',
        { user: 'user' },
      ],
    },
  })

  return (log: FollowUpActionLog) => {
    const user = log.user?.fullName ?? translations.system
    if (log.action === 'REMINDER_SENT') {
      // Automatic reminders go to the driver and the responsible users at once
      // and carry no recipient; a leader's reminder names the one it went to.
      return log.secondaryUser
        ? translations.reminderSentTo({
            user,
            target: log.secondaryUser.fullName,
          })
        : translations.reminderSent
    }
    const describe =
      translations.actions[log.action as keyof typeof translations.actions]
    if (!describe) return log.action
    return describe({ user, name: log.attachmentName ?? '' })
  }
}

export const InfringementActionLogCard: React.FC<{
  actionLogs: FollowUpInfringement['actionLogs']
}> = ({ actionLogs }) => {
  const translations = useTranslate({
    actionLog: 'issues.action-log',
    noActivity: 'absence.details.log.no-activity',
  })
  const actionText = useFollowUpActionText()

  return (
    <InfringementDetailCard
      type="logs"
      title={translations.actionLog}
      icon="clock-rotate-left"
    >
      {actionLogs.length ? (
        <ul className="divide-y divide-stone-200">
          {actionLogs.toReversed().map(log => (
            <li key={log.id} className="px-5 py-3 text-sm sm:px-6">
              <span className="text-stone-500">
                {format(new Date(log.createdAt), 'dd.MM.yyyy HH:mm')}:
              </span>{' '}
              <span className="text-stone-700">{actionText(log)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-5 text-sm text-stone-500 sm:px-6">
          {translations.noActivity}
        </p>
      )}
    </InfringementDetailCard>
  )
}
